import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { formatDate, getClassNames, noOp } from '../../../utils/util';

import ContractOffer from './contract-offer/ContractOffer';
import ContractTerms from './contract-terms/ContractTerms';
import Button from '../button/Button';
import Modal from '../modal/Modal';

import './contract.scss';

const Contract = ({
  dealNumber,
  currentVersion,
  previousVersion,
  product,
  role,
  buyer,
  seller,
  involvedPeople,
  latest,
  amendContract,
  updateContractStatus,
  downloadDocument,
  cancelContract,
  reviewDocument,
  showPopup,
}) => {
  const [open, setOpen] = useState(false);
  const [startCloseAnimation, setStartCloseAnimation] = useState(true);
  const [title, setTitle] = useState('');
  const [amendedBy, setAmendedBy] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);

  useEffect(() => {
    if (currentVersion) {
      const { version } = currentVersion;

      if (version === '0') {
        setTitle('Opening Negotiations');
      } else {
        setTitle(`#${dealNumber} Contract v${version}`);
      }
    }
  }, [currentVersion, dealNumber]);

  useEffect(() => {
    if (currentVersion) {
      const { organisationExternalReference, creatorExternalReference } = currentVersion;
      const creator = involvedPeople.find((person) => person.externalReference === creatorExternalReference);

      if (buyer && buyer.externalReference === organisationExternalReference) {
        setAmendedBy(`${buyer.companyName}, ${creator.name}`);
      } else if (seller) {
        setAmendedBy(`${seller.companyName}, ${creator.name}`);
      }
    }
  }, [currentVersion, buyer, seller, involvedPeople]);

  useEffect(() => {
    if (latest) {
      setOpen(true);
      setStartCloseAnimation(false);
    }
  }, [latest]);

  const handleOverviewClick = () => {
    let timeout;

    if (open) {
      setStartCloseAnimation(true);
      timeout = setTimeout(() => setOpen(false), 300);
    } else {
      setOpen(true);
      setStartCloseAnimation(false);
    }

    return () => clearTimeout(timeout);
  };

  return (
    <section className="contract">
      <section
        className={getClassNames('contract__overview', { open: !startCloseAnimation })}
        onClick={handleOverviewClick}
      >
        <div>
          <p className="contract__overview__title">{title}</p>
          <div>
            <p className="contract__overview__amended-by">
              <b>Last Amended:</b> {amendedBy}
            </p>
            <p className="contract__overview__date">{formatDate(currentVersion.creationDate, true)}</p>
          </div>
        </div>
        <div className="contract__overview__icon-container">
          <div className="contract__overview__icon" />
          <div className={getClassNames('contract__overview__icon', { open: !startCloseAnimation })} />
        </div>
      </section>
      {open && (
        <section className={getClassNames('contract__details', { close: startCloseAnimation })}>
          {!previousVersion && (
            <div className="contract__original-offer">
              <Button onClick={() => setShowProductModal(true)}>View Original Listing Offer</Button>
            </div>
          )}

          <Modal show={showProductModal} onClose={() => setShowProductModal(false)}>
            <ContractOffer product={product} onClose={() => setShowProductModal(false)} />
          </Modal>

          <ContractTerms
            dealNumber={dealNumber}
            currentVersion={currentVersion}
            previousVersion={previousVersion}
            product={product}
            role={role}
            buyer={buyer}
            seller={seller}
            latest={latest}
            amendContract={amendContract}
            updateContractStatus={updateContractStatus}
            downloadDocument={downloadDocument}
            cancelContract={cancelContract}
            reviewDocument={reviewDocument}
            showPopup={showPopup}
          />
        </section>
      )}
    </section>
  );
};

Contract.propTypes = {
  dealNumber: PropTypes.string.isRequired,
  currentVersion: PropTypes.shape({}).isRequired,
  previousVersion: PropTypes.shape({}),
  product: PropTypes.shape({}).isRequired,
  role: PropTypes.string,
  buyer: PropTypes.shape({}).isRequired,
  seller: PropTypes.shape({}).isRequired,
  latest: PropTypes.bool,
  amendContract: PropTypes.func.isRequired,
  updateContractStatus: PropTypes.func,
  downloadDocument: PropTypes.func,
  cancelContract: PropTypes.func,
  reviewDocument: PropTypes.func,
  showPopup: PropTypes.shape({}).isRequired,
};

Contract.defaultProps = {
  previousVersion: undefined,
  role: undefined,
  latest: false,
  updateContractStatus: noOp,
  downloadDocument: noOp,
  cancelContract: noOp,
  reviewDocument: noOp,
};

export default Contract;
